import classNames from "classnames";
import { COMMENT_TYPE } from "store/apis/notes";
import { AddCommentPop } from "components/AddComment/AddComment";
import { AddCommentAnchor } from "components/AddCommentPopoverAnchor/AddCommentPopoverAnchor";
import { AddCommentPopoverRoot } from "components/AddCommentPopoverRoot/AddCommentPopoverRoot";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import { Button } from "components/DesignSystem/Button/Button";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import { Padlock } from "components/icons/Padlock";
import { useHasWritePermission } from "hooks/useHasWritePermission";
import { WRITE_PERMISSION_KEYS } from "constants/permissions";
import { HeadCellContent } from "components/DesignSystem/HeadCellContent/HeadCellContent";
import Popover from "components/DesignSystem/Popover/Popover";
import Table from "components/DesignSystem/Table/V2/Table";
import { ArrowRight } from "components/icons/ArrowRight";
import { AnimatePresence, motion } from "framer-motion";
import { useAppSelector } from "hooks/useAppSelector";
import { CSSProperties, useEffect, useMemo, useState } from "react";
import ThreeDots from "static/images/ThreeDots.svg";
import {
  createColumnHelper,
  ExpandedState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  Row,
  useReactTable,
} from "react-table-8.10.7";
import { GeneralLedger } from "types/Models/books";
import { getCommonPinningStyles } from "utils/getCommonPinningStyles";
import { getPinnedShadowClass } from "utils/getPinnedShadowClass";
import { stopPropagation } from "utils/stopPropagation";
import {
  buildTrialBalanceData,
  TrialBalanceRow,
} from "../utils/trialBalanceView";

type TrialBalanceViewProps = {
  data: GeneralLedger[] | Record<string, GeneralLedger[]> | undefined;
  groupBy: string | null;
};

const COLUMN_IDS = {
  CATEGORY: "category",
  CREDITS: "credits",
  DEBITS: "debits",
  ACTIONS: "actions",
} as const;

const FIXED_WIDTH_COLUMNS: string[] = [
  COLUMN_IDS.CREDITS,
  COLUMN_IDS.DEBITS,
  COLUMN_IDS.ACTIONS,
];

export const getColumnWidthStyle = ({
  size,
  isGroupedByMonth,
  isPinnedColumn,
  isFixedWidthColumn,
}: {
  size: number;
  isGroupedByMonth: boolean;
  isPinnedColumn: string | false;
  isFixedWidthColumn: boolean;
}): CSSProperties => {
  if (isGroupedByMonth && isPinnedColumn) {
    return { width: size, maxWidth: size };
  }
  if (isGroupedByMonth) {
    return { minWidth: size };
  }
  if (isFixedWidthColumn) {
    return { width: size };
  }
  return {};
};

const columnHelper = createColumnHelper<TrialBalanceRow>();

const categoryColumn = columnHelper.accessor("name", {
  id: COLUMN_IDS.CATEGORY,
  header: () => <HeadCellContent title="Category" align="left" />,
  cell: ({ row, getValue }) => {
    const name = getValue();
    const hasChildren = row.original.types && row.original.types.length > 0;

    return (
      <div
        style={{ paddingLeft: `${row.depth * 16}px` }}
        className="t-flex t-items-center t-gap-2"
      >
        <div
          className={classNames(
            "t-transform t-transition t-duration-300 t-ease-in-out",
            {
              "t-rotate-90": row.getIsExpanded(),
              "t-invisible": !hasChildren,
            }
          )}
        >
          <ArrowRight stroke="1.5" color="currentColor" />
        </div>
        <span className="t-text-body-sm t-text-text-60">
          {row.original.identifier} - {name}
        </span>
      </div>
    );
  },
});

const getTrialBalanceAmount = (row: Row<TrialBalanceRow>) => {
  const hasChildren = row.original.types && row.original.types.length > 0;
  return hasChildren
    ? row.original.summary_amount || 0
    : row.original.trial_balance_amount || 0;
};

const creditsColumn = columnHelper.accessor("total_credits", {
  id: COLUMN_IDS.CREDITS,
  size: 100,
  header: () => <HeadCellContent title="Credit" align="right" />,
  cell: ({ row }) => {
    const amount = getTrialBalanceAmount(row);
    const isDebit = row.original?.is_debit_account;

    return (
      <div className="t-flex t-justify-end t-text-body-sm t-text-text-60">
        {isDebit ? "" : <AmountSuperScript amount={amount} />}
      </div>
    );
  },
});

const debitsColumn = columnHelper.accessor("total_debits", {
  id: COLUMN_IDS.DEBITS,
  size: 100,
  header: () => <HeadCellContent title="Debit" align="right" />,
  cell: ({ row }) => {
    const amount = getTrialBalanceAmount(row);
    const isDebit = row.original?.is_debit_account;

    return (
      <div className="t-flex t-justify-end t-text-body-sm t-text-text-60">
        {isDebit ? <AmountSuperScript amount={amount} /> : ""}
      </div>
    );
  },
});

const getActionsColumn = (hasWritePermission: boolean) =>
  columnHelper.display({
    id: COLUMN_IDS.ACTIONS,
    size: 55,
    header: () => null,
    cell: (info) => {
      if (!hasWritePermission) {
        return (
          <div className="t-pointer-events-none">
            <Button customType="ghost_icon" size="small" disabled>
              <Padlock />
            </Button>
          </div>
        );
      }

      return (
        <div onClick={stopPropagation}>
          <AddCommentPopoverRoot contentId={info.row.original.uuid}>
            <Dropdown.Root>
              <AddCommentAnchor contentId={info.row.original.uuid}>
                <Dropdown.Trigger asChild>
                  <div>
                    <Button customType="ghost_icon" size="small">
                      <img
                        src={ThreeDots}
                        alt="Action"
                        className="t-select-none"
                      />
                    </Button>
                  </div>
                </Dropdown.Trigger>
              </AddCommentAnchor>
              <Popover.Portal>
                <AddCommentPop
                  commentType={COMMENT_TYPE.GENERAL_LEDGER_COMMENT}
                  contentId={info.row.original.uuid}
                />
              </Popover.Portal>
              <Dropdown.Portal>
                <Dropdown.Content onClick={stopPropagation}>
                  <Popover.Trigger asChild>
                    <Dropdown.Item>Add comment</Dropdown.Item>
                  </Popover.Trigger>
                </Dropdown.Content>
              </Dropdown.Portal>
            </Dropdown.Root>
          </AddCommentPopoverRoot>
        </div>
      );
    },
  });

const getMonthColumns = (months: string[]) =>
  months.map((month) =>
    columnHelper.group({
      id: month,
      header: () => <HeadCellContent title={month} align="right" />,
      columns: [
        columnHelper.accessor("uuid", {
          id: `${month}_credits`,
          size: 120,
          header: () => <HeadCellContent title="Credit" align="right" />,
          cell: ({ row }) => {
            const monthCategory = row.original.monthData?.[month];
            const amount = monthCategory?.trial_balance_amount;
            const isDebit = monthCategory?.is_debit_account;

            if (amount === null || amount === undefined) return;

            return (
              <div className="t-flex t-justify-end t-text-body-sm t-text-text-60">
                {isDebit ? "" : <AmountSuperScript amount={amount} />}
              </div>
            );
          },
        }),
        columnHelper.accessor("uuid", {
          id: `${month}_debits`,
          size: 120,
          header: () => <HeadCellContent title="Debit" align="right" />,
          cell: ({ row }) => {
            const monthCategory = row.original.monthData?.[month];
            const amount = monthCategory?.trial_balance_amount;
            const isDebit = monthCategory?.is_debit_account;
            if (amount === null || amount === undefined) return;

            return (
              <div className="t-flex t-justify-end t-text-body-sm t-text-text-60">
                {isDebit ? <AmountSuperScript amount={amount} /> : ""}
              </div>
            );
          },
        }),
      ],
    })
  );

const TrialBalanceTableRow = ({
  row,
  isGroupedByMonth,
  isHighlighted,
}: {
  row: Row<TrialBalanceRow>;
  isGroupedByMonth: boolean;
  isHighlighted: boolean;
}) => {
  const hasChildren = row.original.types && row.original.types.length > 0;

  return (
    <motion.tr
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: "auto" }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ duration: 0.2, ease: "easeInOut" }}
      layout
      className={classNames(
        "t-border t-border-solid t-border-x-0 t-border-t-0 t-border-neutral-0 t-group t-px-3 t-text-body-sm",
        {
          "t-cursor-pointer hover:t-bg-surface-lighter-grey": hasChildren,
          "animate-transaction-row": isHighlighted,
        }
      )}
      onClick={() => {
        if (hasChildren) {
          row.toggleExpanded();
        }
      }}
    >
      {row.getVisibleCells().map((cell) => {
        const styles = isGroupedByMonth
          ? getCommonPinningStyles(cell.column)
          : {};
        const { boxShadow } = isGroupedByMonth
          ? getPinnedShadowClass(cell.column)
          : { boxShadow: undefined };
        const isPinnedColumn = cell.column.getIsPinned();
        const isFixedWidthColumn =
          !isGroupedByMonth && FIXED_WIDTH_COLUMNS.includes(cell.column.id);

        const widthStyle = getColumnWidthStyle({
          size: cell.column.getSize(),
          isGroupedByMonth,
          isPinnedColumn,
          isFixedWidthColumn,
        });

        return (
          <Table.Cell
            key={cell.id}
            style={{
              ...styles,
              ...widthStyle,
            }}
            className={classNames(boxShadow, {
              "t-border-r t-border-neutral-0":
                cell.column.id.endsWith(COLUMN_IDS.CREDITS) ||
                (isGroupedByMonth &&
                  cell.column.id.endsWith(COLUMN_IDS.DEBITS)),
              "!t-z-[3]": isPinnedColumn,
              "group-hover:!t-bg-surface-lighter-grey":
                isPinnedColumn && hasChildren,
            })}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </Table.Cell>
        );
      })}
    </motion.tr>
  );
};

const getParentTree = (
  rows: TrialBalanceRow[],
  targetId: string,
  parents: string[] = []
): string[] | undefined => {
  for (const row of rows) {
    if (row.uuid === targetId) return parents;
    if (row.types) {
      const found = getParentTree(row.types as TrialBalanceRow[], targetId, [
        ...parents,
        row.uuid,
      ]);
      if (found) return found;
    }
  }
};

export const TrialBalanceView = ({ data, groupBy }: TrialBalanceViewProps) => {
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const openComment = useAppSelector((state) => state.openComment.openComment);
  const hasWritePermission = useHasWritePermission(
    WRITE_PERMISSION_KEYS.ACCOUNTING
  );

  const isGroupedByMonth = Boolean(groupBy);
  const isMonthlyData = data && !Array.isArray(data);
  const months = isMonthlyData ? Object.keys(data) : [];

  const tableData = useMemo(() => {
    if (!data) return [];
    return buildTrialBalanceData(data, isGroupedByMonth);
  }, [data, isGroupedByMonth]);

  useEffect(() => {
    if (!openComment) return;
    const parents = getParentTree(tableData, openComment);
    if (parents && parents.length > 0) {
      setExpanded((prev) =>
        typeof prev === "object"
          ? parents.reduce((acc, id) => ({ ...acc, [id]: true }), prev || {})
          : prev
      );
    }
  }, [openComment, tableData]);

  const monthsKey = months.join(",");

  const columns = useMemo(() => {
    const monthColumns = isGroupedByMonth
      ? getMonthColumns(months)
      : [creditsColumn, debitsColumn];

    return [
      {
        ...categoryColumn,
        size: isGroupedByMonth ? 325 : undefined,
      },
      ...monthColumns,
      getActionsColumn(hasWritePermission),
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGroupedByMonth, monthsKey, hasWritePermission]);

  const columnVisibility = useMemo(
    () => ({
      [COLUMN_IDS.CREDITS]: !isGroupedByMonth,
      [COLUMN_IDS.DEBITS]: !isGroupedByMonth,
    }),
    [isGroupedByMonth]
  );

  const table = useReactTable({
    data: tableData,
    columns,
    state: {
      expanded,
      columnVisibility,
      columnPinning: isGroupedByMonth
        ? { left: [COLUMN_IDS.CATEGORY], right: [COLUMN_IDS.ACTIONS] }
        : {},
    },
    onExpandedChange: setExpanded,
    getSubRows: (row) => row.types as TrialBalanceRow[],
    getRowId: (row) => row.uuid,
    getExpandedRowModel: getExpandedRowModel(),
    getCoreRowModel: getCoreRowModel(),
  });

  if (!data) {
    return null;
  }

  return (
    <div>
      <Table.Container
        className={classNames({
          "t-overflow-scroll scrollbar-visible t-scroll-auto t-w-full":
            isGroupedByMonth,
        })}
      >
        <Table.Content
          className={classNames({ "t-w-full": !isGroupedByMonth })}
        >
          <Table.Head>
            {table.getHeaderGroups().map((headerGroup) => {
              const isGroupHeader = headerGroup.depth === 0 && isGroupedByMonth;

              return (
                <Table.HeadRow
                  key={headerGroup.id}
                  className="t-border t-border-solid t-border-x-0 t-border-t-0 t-border-neutral-10 t-bg-surface-lighter-grey"
                >
                  {headerGroup.headers.map((header) => {
                    const styles = isGroupedByMonth
                      ? getCommonPinningStyles(header.column)
                      : {};
                    const { boxShadow } = isGroupedByMonth
                      ? getPinnedShadowClass(header.column)
                      : { boxShadow: undefined };
                    const isPinnedColumn = header.column.getIsPinned();

                    const isFixedWidthColumn =
                      !isGroupedByMonth &&
                      FIXED_WIDTH_COLUMNS.includes(header.column.id);

                    const widthStyle = getColumnWidthStyle({
                      size: header.getSize(),
                      isGroupedByMonth,
                      isPinnedColumn,
                      isFixedWidthColumn,
                    });

                    return (
                      <Table.HeadCell
                        key={header.id}
                        isUpperCase={false}
                        colSpan={header.colSpan}
                        style={{
                          ...styles,
                          ...widthStyle,
                        }}
                        className={classNames(boxShadow, {
                          "t-border-r t-border-neutral-0":
                            header.column.id.endsWith(COLUMN_IDS.CREDITS) ||
                            (isGroupedByMonth &&
                              (isGroupHeader ||
                                header.column.id.endsWith(COLUMN_IDS.DEBITS))),
                          "!t-text-surface-lighter-grey !t-border-r-0":
                            isGroupHeader &&
                            header.column.id === COLUMN_IDS.CATEGORY,
                          "!t-bg-surface-lighter-grey": isPinnedColumn,
                          "!t-z-[3]": isPinnedColumn,
                        })}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </Table.HeadCell>
                    );
                  })}
                </Table.HeadRow>
              );
            })}
          </Table.Head>
          <Table.Body>
            <AnimatePresence initial={false}>
              {table.getRowModel().rows.map((row) => (
                <TrialBalanceTableRow
                  key={row.id}
                  row={row}
                  isGroupedByMonth={isGroupedByMonth}
                  isHighlighted={openComment === row.original.uuid}
                />
              ))}
            </AnimatePresence>
          </Table.Body>
        </Table.Content>
      </Table.Container>
    </div>
  );
};
